import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDebouncedApiEffect } from "../../../hooks/useDebouncedApiEffect";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import { tokens } from "../../../public/tokens";
import {
  DeleteCampusBuildingRooms,
  GetCampusBuildingRoomsList,
  GetCampusBuildingsList,
  PostSaveCampusBuildingRoom,
  PutSaveCampusBuildingRoom,
} from "../../../services/CampusBuildingService";
import {
  getOperatingHoursRows,
  mapBuildingOrRoomsForTable,
  OPERATING_HOUR_DAYS,
} from "../../../utils/CampusBuildingsUtil";
import { handleTableScroll } from "../../../utils/commonFunction";
import Button from "../../common/Button/Button";
import DataTableComponent from "../../common/DataTableComponent";
import AddEditRoomModal from "./AddEditRoomModal";
import { createLinkButtonCell, getTableHeader } from "./TableComponents";

const { colors, typography, effects, globals } = tokens;
const { neutral, states, text: textColors, ui, surfaces } = colors;

const cardStylePadding = "21px";

const operatingDaysOptions = OPERATING_HOUR_DAYS.map((b) => ({ value: b.key, label: b.label }));

// Room type colour mapping – using tokens with fallback hex values
const ROOM_TYPE_COLORS = {
  classroom: states.success.default,                    // #43B275
  practical_venue: states.warning.default,              // #F78F32
  studio: colors.brand?.plum_orchid?.[500] || "#A8489B",
  boardroom: colors.brand?.tide_blue?.[700] || "#2F909B",
  multi_purpose_hall: colors.brand?.karoo_gold?.[700] || "#B99B19",
};

const getRoomTypeColor = (type) => {
  if (!type) return textColors.primary;
  const key = type.toLowerCase().replace(/\s+/g, "_");
  return ROOM_TYPE_COLORS[key] || textColors.primary;
};

// Operating hours cell renderer – matches design (multi-line)
const renderOperatingHoursCell = (row) => {
  const rows = getOperatingHoursRows(row.operatingHoursObj);
  if (!rows.length) {
    return <span>-</span>;
  }
  if (rows.length === 1 && rows[0].label === "Inherited from Building") {
    return <span>{rows[0].label}</span>;
  }
  return (
    <div style={{ lineHeight: "1.4", fontSize: typography.fontSize["lg-14px"] }}>
      {rows.map((item, idx) => (
        <div key={idx} style={{ display: "flex", gap: "8px" }}>
          <span style={{ width: "70px", color: textColors.secondary }}>{item.label}</span>
          <span>{item.time}</span>
        </div>
      ))}
    </div>
  );
};

// Custom DataTable styles – matches design spacing/header styling
const customStyles = {
  headRow: {
    style: {
      backgroundColor: neutral.white,
      borderBottom: `1px solid ${ui.border}`,
      minHeight: "46px",
    },
  },
  headCells: {
    style: {
      paddingLeft: "10px",
      paddingRight: "10px",
      fontSize: "14px",
      fontWeight: typography.fontWeights.semibold,
      color: textColors.primary,
    },
  },
  rows: {
    style: {
      minHeight: "45px",
      borderBottom: `1px solid ${ui.border}`,
      fontSize: "14px",
      fontFamily: globals["font-family"],
      backgroundColor: neutral.white,
      "&:nth-child(even)": {
        backgroundColor: surfaces["page-background"],
      },
    },
  },
  cells: {
    style: {
      paddingLeft: "10px",
      paddingRight: "10px",
      fontSize: typography.fontSize["lg-14px"],
      fontFamily: globals["font-family"],
      color: textColors.primary,
    },
  },
  pagination: {
    style: {
      borderTop: "none",
      paddingTop: "16px",
      fontFamily: globals["font-family"],
    },
  },
};

const RoomList = () => {
  const search = useLocation().search;
  const campusId = Number(new URLSearchParams(search).get("id")) || null;

  const [rooms, setRooms] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFirstTime, setIsLoadingFirstTime] = useState(true);

  const [total, setTotal] = useState(0);
  const [tableState, setTableState] = useState({
    page: 1,
    perPage: 10,
    sortKey: "room_name",
    sortOrder: "asc",
  });

  // Filters
  const [searchValue, setSearchValue] = useState("");
  const [typeFilter, setTypeFilter] = useState({
    arr: [],
    checkObj: {}
  });
  const [buildingFilter, setBuildingFilter] = useState({
    arr: [],
    checkObj: {}
  });
  const [operatingDayFilter, setOperatingDayFilter] = useState({
    arr: [],
    checkObj: {}
  });

  const [showRoomModal, setShowRoomModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [savingRoom, setSavingRoom] = useState(false);

  // Scroll handling
  useEffect(() => {
    handleTableScroll();
  }, [isLoading]);

  // Fetch buildings for dropdown (needed for filter and modal)
  useEffect(() => {
    const fetchBuildings = async () => {
      if (!campusId) return;
      try {
        const res = await GetCampusBuildingsList(campusId);
        if (res.data?.campusBuildingDetails) {
          const mapped = res.data.campusBuildingDetails.map(mapBuildingOrRoomsForTable);
          setBuildings(mapped);
        }
      } catch (error) {
        console.error("Failed to fetch buildings", error);
      }
    };
    fetchBuildings();
  }, [campusId]);

  // Fetch rooms from backend with current params (server-side filtering)
  const fetchRooms = useCallback(async (args) => {
    const cancelToken = args?.cancelToken;
    if (!campusId) {
      return;
    }
    setIsLoading(true);
    try {
      const params = {
        start: (tableState.page - 1) * tableState.perPage,
        count: tableState.perPage,
        sort: tableState.sortKey,
        order: tableState.sortOrder,
        search: searchValue || undefined,
        type: typeFilter.arr.length ? typeFilter.arr : undefined,
        building: buildingFilter.arr.length ? buildingFilter.arr : undefined,
        operatingDay: operatingDayFilter.arr.length
          ? operatingDayFilter.arr
          : undefined
      };
      const res = await GetCampusBuildingRoomsList(campusId, params, cancelToken);
      if (res.data?.campusBuildingRoomsList) {
        const mapped = res.data.campusBuildingRoomsList.map(mapBuildingOrRoomsForTable);
        setRooms(mapped);
        if (res.data.range) {
          setTotal(res.data.range.total);
        }
      } else {
        setRooms([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching rooms:", error);
      Swal.fire("Error!", "Failed to load rooms.", "error");
    } finally {
      setIsLoading(false);
      setIsLoadingFirstTime(false);
    }
  }, [campusId, tableState, searchValue, typeFilter, buildingFilter, operatingDayFilter]);

  // Purpose: When search or sort changes, it must refetch
  useDebouncedApiEffect(
    fetchRooms,
    [campusId,
      tableState,
      typeFilter.arr,
      buildingFilter.arr,
      operatingDayFilter.arr,
      searchValue
    ]
  );

  // Unique options for dropdowns (derived from fetched data)
  const typeOptions = useMemo(() => {
    const types = rooms.map((r) => r.type).filter(Boolean);

    return [...new Set(types)].map(type => ({
      label: type,
      value: type
    }));
  }, [rooms]);

  const buildingOptions = useMemo(() => {
    // Use all buildings (not just those with rooms) to allow filtering
    return buildings.map((b) => ({ value: b.id, label: b.name }));
  }, [buildings]);

  const handleDeleteRoom = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete "${row.room_name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: states.alert.default,
      cancelButtonColor: "var(--button-color)",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const res = await DeleteCampusBuildingRooms(row.id);
          return !!res.data?.deletedBuildingRoom?.id;
        } catch (error) {
          Swal.showValidationMessage(`Request failed: ${error}`);
          return false;
        }
      },
    });
    if (result.isConfirmed) {
      await fetchRooms(); // refresh list after deletion
      Swal.fire("Deleted!", "Room deleted.", "success");
    }
  };

  const handleSaveRoom = async (roomData) => {
    setSavingRoom(true);
    try {
      if (editingRoom) {
        await PutSaveCampusBuildingRoom(roomData);
      } else {
        await PostSaveCampusBuildingRoom(roomData);
      }
      await fetchRooms(); // refresh with current filters
      Swal.fire("Success!", `Room ${editingRoom ? "updated" : "added"} successfully.`, "success");
      setShowRoomModal(false);
      setEditingRoom(null);
    } catch (error) {
      console.error("Error saving room:", error);
      Swal.fire("Error!", "Failed to save room.", "error");
    } finally {
      setSavingRoom(false);
    }
  };

  // Table headings style
  const headingStyle = {
    color: textColors.primary,
    fontSize: typography.fontSize["base-16px"],
    fontWeight: typography.fontWeights.semibold,
    fontFamily: globals["font-family"],
    lineHeight: "16px",
  };

  const columns = useMemo(
    () => [
      {
        name: getTableHeader("Name", tableState.sortKey === "room_name"),
        selector: "room_name",
        sortField: "room_name",
        sortable: true,
        width: "200px",
        cell: createLinkButtonCell({
          onClick: (row) => {
            const fullRoom = rooms.find((r) => r.id === row.id);
            setEditingRoom(fullRoom);
            setShowRoomModal(true);
          },
          getLabel: (row) => row.room_name,
          getTitle: (row) => row.room_name
        })
      },
      {
        name: getTableHeader("Type", tableState.sortKey === "type"),
        selector: "type",
        sortField: "type",
        sortable: true,
        width: "180px",
        cell: (row) => {
          const color = getRoomTypeColor(row.type);
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  backgroundColor: color,
                  display: "inline-block",
                }}
              />
              <span style={{ color, fontSize: "14px" }}>
                {row.type?.replaceAll("_", " ") || "-"}
              </span>
            </div>
          );
        }
      },
      {
        name: getTableHeader("Campus Building", tableState.sortKey === "building_name"),
        selector: "building_name",
        sortField: "building_name",
        sortable: true,
        width: "150px",
        cell: (row) => {
          const bgColor = row.grouping_color || ui.primary;
          return (
            <span
              className="as-widget textLimit50"
              style={{
                backgroundColor: bgColor,
                color: neutral.white,
                borderRadius: globals["border-radius"],
                padding: "4px 12px",
                fontSize: typography.fontSize["md-12px"],
                fontWeight: typography.fontWeights.bold,
                display: "inline-block",
              }}
            >
              {row.building_name || "-"}
            </span>
          );
        }
      },
      {
        name: <div style={headingStyle}>Operating Hours</div>,
        sortable: false,
        width: "190px",
        cell: renderOperatingHoursCell
      },
      {
        name: getTableHeader("Room#", tableState.sortKey === "room_no"),
        selector: "room_no",
        sortField: "room_no",
        sortable: true,
        width: "120px",
        cell: (row) => row.room_no || "-"
      },
      {
        name: getTableHeader("Capacity", tableState.sortKey === "capacity"),
        selector: "capacity",
        sortField: "capacity",
        sortable: true,
        width: "100px",
        cell: (row) => row.capacity || "-"
      },
      {
        name: getTableHeader("Description", tableState.sortKey === "room_description"),
        selector: "room_description",
        sortField: "room_description",
        sortable: true,
        grow: 1,
        minWidth: "300px",
        cell: (row) => (
          <span title={row.room_description} style={{ display: "block" }}>
            {row.room_description || "-"}
          </span>
        )
      },
      {
        name: getTableHeader("Actions"),
        sortable: false,
        width: "100px",
        cell: (row) => (
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-start" }}>
            <Button
              className="btn btn-primary campus-action-button"
              variant="primary"
              iconOnly
              iconLeft={
                <i
                  className="fal fa-edit"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: 0,
                    lineHeight: 1,
                  }}
                />
              }
              onClick={() => {
                const fullRoom = rooms.find((r) => r.id === row.id);
                setEditingRoom(fullRoom);
                setShowRoomModal(true);
              }}
              title="Edit"
            />

            <Button
              className="btn btn-danger campus-action-button"
              variant="danger"
              iconOnly
              iconLeft={
                <i
                  className="fal fa-trash-alt"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: 0,
                    lineHeight: 1,
                  }}
                />
              }
              onClick={() => handleDeleteRoom(row)}
              title="Delete"
            />
          </div>
        )
      }
    ],
    [rooms, headingStyle, handleDeleteRoom]
  );

  if (isLoadingFirstTime) {
    return <SkeletonTicketList />;
  }

  return (
    <>
      <DataTableComponent
        data={rooms}
        columns={columns}
        state={tableState}
        setState={setTableState}
        setSearch={setSearchValue}
        totalRows={total}
        loading={isLoading}
        isExport={false}
        btnStyleOverride={{ borderRadius: globals["border-radius"] }}
        containerStyle={{
          borderRadius: globals["border-radius"],
          padding: cardStylePadding
        }}
        filterBtnSpacing="4px"
        otherTableProps={{
          highlightOnHover: true,
          customStyles,
          responsive: true
        }}
        filters={[
          {
            filterName: "Type",
            optionArr: typeOptions,
            state: typeFilter,
            setState: setTypeFilter,
            isOptionReversed: false
          },
          {
            filterName: "Campus Building",
            optionArr: buildingOptions,
            state: buildingFilter,
            setState: setBuildingFilter,
            isOptionReversed: false
          },
          {
            filterName: "Operating Days",
            optionArr: operatingDaysOptions,
            state: operatingDayFilter,
            setState: setOperatingDayFilter,
            isOptionReversed: false
          }
        ]}
        tableButton={[
          <Button
            className="btn btn-primary"
            variant="primary"
            iconLeft={<i className="fal fa-plus" />}
            onClick={() => {
              setEditingRoom(null);
              setShowRoomModal(true);
            }}
            style={{ borderRadius: globals["border-radius"] }}
          >
            Add Room
          </Button>
        ]}
      />

      {/* Add/Edit Room Modal */}
      <AddEditRoomModal
        campusId={campusId}
        show={showRoomModal}
        onHide={() => {
          setShowRoomModal(false);
          setEditingRoom(null);
        }}
        room={editingRoom}
        buildings={buildings}
        onSave={handleSaveRoom}
        saving={savingRoom}
      />
    </>
  );
};

export default RoomList;
